/**
 * ViewportLayoutService - Manages viewport layout arrangements.
 *
 * Provides predefined layout presets and manages the arrangement of viewports
 * within a container element. Uses ViewportService for actual viewport creation.
 */

import { ViewportService } from './viewport-service';
import { PubSubService } from '../core/pubsub-service';
import type { ServiceContext, ServiceRegistration } from '../core/types';
import type { ViewMode } from '../display';
import { InteractionService } from '../interaction';
import { ToolGroupManager } from '../tools/tool-group-manager';
import type { IViewport } from '../viewports/types';
import { ViewportType, AnyViewportOptions, VIEWPORT_TYPE_TO_VIEW_MODE } from '../viewports/types';

// ============================================================================
// Types
// ============================================================================

export type ViewportSlot = 'toolbar' | 'panel' | 'slider' | 'wl' | 'carm';

interface SlotConfigBase {
    /** Unique identifier for this slot */
    id: string;
    /** Display label */
    label: string;
    /** Grid row (1-based) */
    row: number;
    /** Grid column (1-based) */
    col: number;
    /** Number of rows to span (default: 1) */
    rowSpan?: number;
    /** Number of columns to span (default: 1) */
    colSpan?: number;
}

/** A slot that creates a VTK viewport with interaction and tools. */
export interface ViewportSlotConfig extends SlotConfigBase {
    slotKind: 'viewport';
    type: ViewportType;
    options?: AnyViewportOptions;
}

/** A slot that provides a plain DOM cell for React portal content. */
export interface PanelSlotConfig extends SlotConfigBase {
    slotKind: 'panel';
    panelId: string;
}

export type SlotConfig = ViewportSlotConfig | PanelSlotConfig;

/**
 * A layout preset defining how viewports are arranged.
 */
export interface LayoutPreset {
    /** Unique name for this layout */
    name: string;
    /** Display label for the layout */
    label: string;
    /** Number of rows in the grid */
    rows: number;
    /** Number of columns in the grid */
    cols: number;
    /** Slot configurations (viewports and panels) */
    viewports: SlotConfig[];
}

/**
 * Event payload when layout changes.
 */
export interface LayoutChangedPayload {
    /** New layout name, or null if cleared */
    layoutName: string | null;
    /** Viewport IDs in the new layout */
    viewportIds: string[];
}

// ============================================================================
// ViewportLayoutService
// ============================================================================

/**
 * Service for managing viewport layouts.
 *
 * Creates viewport container elements and arranges them in a grid layout.
 * Uses ViewportService for actual VTK viewport creation.
 *
 * @example
 * ```typescript
 * const layoutService = serviceManager.getService<ViewportLayoutService>(
 *     ViewportLayoutService.SERVICE_NAME
 * );
 *
 * // Set up the container
 * layoutService.setContainer(document.getElementById('viewer-container'));
 *
 * // Apply a layout
 * layoutService.applyLayout(LAYOUT_EVAR_DEFAULT);
 *
 * // Get a specific viewport
 * const volumeViewport = layoutService.getViewport('volume-3d');
 * ```
 */
export class ViewportLayoutService extends PubSubService {
    static readonly SERVICE_NAME = 'ViewportLayoutService';

    static readonly REGISTRATION: ServiceRegistration = {
        name: ViewportLayoutService.SERVICE_NAME,
        factory: (context: ServiceContext) => new ViewportLayoutService(context),
    };

    readonly EVENTS = {
        /**
         * Emitted before the current layout's viewports are destroyed.
         * Subscribers needing live viewports (e.g. cancelling a C-arm draft)
         * must react here, not on LAYOUT_CHANGED.
         */
        LAYOUT_CLEARING: 'viewportlayout:layout:clearing',
        /** Emitted when the layout changes */
        LAYOUT_CHANGED: 'viewportlayout:layout:changed',
        /** Emitted when a viewport container is created */
        VIEWPORT_CONTAINER_CREATED: 'viewportlayout:viewport:container:created',
    } as const;

    private readonly viewportService: ViewportService;
    private readonly interactionService: InteractionService;
    private readonly toolGroupManager: ToolGroupManager;

    /** The root container element for layouts */
    private container: HTMLElement | null = null;

    /** Currently applied layout */
    private currentLayout: LayoutPreset | null = null;

    /** Map of viewport IDs to their container elements */
    private viewportContainers = new Map<string, HTMLElement>();

    /** Map of viewport IDs to created viewports */
    private viewports = new Map<string, IViewport>();

    /** Map slotId -> slotName -> HTMLElement */
    private viewportSlots = new Map<string, Map<ViewportSlot, HTMLElement>>();

    constructor(context: ServiceContext) {
        super();
        this.viewportService = context.services.getService<ViewportService>(ViewportService.SERVICE_NAME);
        this.interactionService = context.services.getService<InteractionService>(
            InteractionService.SERVICE_NAME
        );
        this.toolGroupManager = context.services.getService<ToolGroupManager>(ToolGroupManager.SERVICE_NAME);
    }

    /**
     * Set the root container element for layouts.
     *
     * @param container - The container element
     */
    setContainer(container: HTMLElement): void {
        this.container = container;
    }

    /**
     * Get the current root container.
     */
    getContainer(): HTMLElement | null {
        return this.container;
    }

    /**
     * Apply a layout preset.
     * Creates the grid structure and viewport containers.
     *
     * @param preset - The layout preset to apply
     */
    applyLayout(preset: LayoutPreset): void {
        if (!this.container) {
            throw new Error('Container not set. Call setContainer() first.');
        }

        // Clear existing layout
        this.clearLayout();

        // Set up grid container (dans le conteneur par défaut)
        this.container.style.display = 'grid';
        this.container.style.gridTemplateRows = `repeat(${preset.rows}, 1fr)`;
        this.container.style.gridTemplateColumns = `repeat(${preset.cols}, 1fr)`;
        this.container.style.gap = '4px';
        this.container.style.width = '100%';
        this.container.style.height = '100%';

        // Create slot cells
        for (const config of preset.viewports) {
            const { wrapper, container: cellContainer } = this.createViewportCell(config);
            this.container.appendChild(wrapper);

            if (config.slotKind === 'viewport') {
                this.viewportContainers.set(config.id, cellContainer);
                try {
                    const viewport = this.viewportService.createViewport(
                        config.id,
                        cellContainer,
                        config.type,
                        config.options
                    );
                    this.viewports.set(config.id, viewport);

                    this.interactionService.initDomInteractionProxy(cellContainer, viewport);
                    this.toolGroupManager.createDefaultToolGroup(config.type, viewport);
                } catch (error) {
                    console.error(`Failed to create viewport ${config.id}:`, error);
                    this.addPlaceholder(cellContainer, config);
                }
            }

            this.broadcast(this.EVENTS.VIEWPORT_CONTAINER_CREATED, {
                id: config.id,
                type: config.slotKind === 'viewport' ? config.type : undefined,
            });
        }

        this.currentLayout = preset;

        this.broadcast(this.EVENTS.LAYOUT_CHANGED, {
            layoutName: preset.name,
            viewportIds: preset.viewports.map((v) => v.id),
        } satisfies LayoutChangedPayload);
    }

    /**
     * Clear the current layout and destroy all viewports.
     */
    clearLayout(): void {
        // Let subscribers clean up while the viewports are still alive
        if (this.currentLayout) {
            this.broadcast(this.EVENTS.LAYOUT_CLEARING, {
                layoutName: this.currentLayout.name,
                viewportIds: [...this.viewports.keys()],
            } satisfies LayoutChangedPayload);
        }

        // Remove viewport-to-toolgroup mappings and destroy viewports
        for (const id of this.viewports.keys()) {
            this.toolGroupManager.removeViewportFromToolGroup(id);
            this.viewportService.destroyViewport(id);
        }
        this.viewports.clear();

        // Remove all viewport containers and slots
        this.viewportContainers.clear();
        this.viewportSlots.clear();

        // Clear the root container
        if (this.container) {
            this.container.innerHTML = '';
            this.container.style.display = '';
            this.container.style.gridTemplateRows = '';
            this.container.style.gridTemplateColumns = '';
            this.container.style.gap = '';
        }

        const prevLayout = this.currentLayout;
        this.currentLayout = null;

        if (prevLayout) {
            this.broadcast(this.EVENTS.LAYOUT_CHANGED, {
                layoutName: null,
                viewportIds: [],
            } satisfies LayoutChangedPayload);
        }
    }

    /**
     * Get a viewport by ID.
     */
    getViewport(id: string): IViewport | undefined {
        return this.viewports.get(id);
    }

    /**
     * Get the container element for a viewport.
     */
    getViewportContainer(id: string): HTMLElement | undefined {
        return this.viewportContainers.get(id);
    }

    /**
     * Get a named slot element for a layout cell.
     *
     * @param slotId - The slot config ID
     * @param slot - The slot name ('toolbar' for viewports, 'panel' for panel cells)
     */
    getSlot(slotId: string, slot: ViewportSlot): HTMLElement | undefined {
        return this.viewportSlots.get(slotId)?.get(slot);
    }

    /**
     * Get the currently applied layout.
     */
    getCurrentLayout(): LayoutPreset | null {
        return this.currentLayout;
    }

    /**
     * Get all viewport IDs in the current layout.
     */
    getViewportIds(): string[] {
        return Array.from(this.viewports.keys());
    }

    /**
     * Get the ViewModes currently active in the layout (one per viewport).
     * Useful for restricting UI controls to only the views actually displayed.
     */
    getActiveViewModes(): ViewMode[] {
        const modes: ViewMode[] = [];
        for (const viewport of this.viewports.values()) {
            const mode = VIEWPORT_TYPE_TO_VIEW_MODE[viewport.type];
            if (mode !== undefined) modes.push(mode);
        }
        return modes;
    }

    /**
     * Resize all viewports to match their containers.
     */
    resizeAll(): void {
        for (const id of this.viewports.keys()) {
            this.viewportService.resizeViewport(id);
        }
        this.viewportService.renderAll();
    }

    /**
     * Reset the service state.
     */
    override reset(): void {
        console.log(`${ViewportLayoutService.SERVICE_NAME} reset called`);
        this.clearLayout();
        this.container = null;
        super.reset();
    }

    // ========================================================================
    // Private Methods
    // ========================================================================

    /**
     * Create a layout cell. For viewport slots: VTK container + toolbar slot.
     * For panel slots: a single panel slot for React portal content.
     */
    private createViewportCell(config: SlotConfig): {
        wrapper: HTMLElement;
        container: HTMLElement;
    } {
        // Wrapper — the grid cell
        const wrapper = document.createElement('div');
        wrapper.id = `viewport-cell-${config.id}`;
        wrapper.style.position = 'relative';
        wrapper.style.overflow = 'hidden';
        wrapper.style.backgroundColor = '#1a1a1a';
        wrapper.style.borderRadius = '4px';
        wrapper.style.minHeight = '200px';
        wrapper.style.gridRow = config.rowSpan ? `${config.row} / span ${config.rowSpan}` : `${config.row}`;
        wrapper.style.gridColumn = config.colSpan
            ? `${config.col} / span ${config.colSpan}`
            : `${config.col}`;

        if (config.slotKind === 'panel') {
            // Panel cell — a single slot for React portal content
            const panelSlot = document.createElement('div');
            panelSlot.dataset.slot = 'panel';
            panelSlot.style.position = 'relative';
            panelSlot.style.width = '100%';
            panelSlot.style.height = '100%';

            wrapper.appendChild(panelSlot);

            this.viewportSlots.set(config.id, new Map([['panel', panelSlot]]));

            return { wrapper, container: panelSlot };
        }

        // Viewport cell — VTK container + toolbar slot
        const container = document.createElement('div');
        container.id = `viewport-container-${config.id}`;
        container.className = 'viewport-container';
        container.style.position = 'absolute';
        container.style.inset = '0';

        const slotCarm = this.createSlot('carm', 35);
        const slotToolbar = this.createSlot('toolbar', 30);
        const slotSlider = this.createSlot('slider', 25);
        const slotWl = this.createSlot('wl', 20);

        wrapper.append(container, slotCarm, slotToolbar, slotSlider, slotWl);

        this.viewportSlots.set(
            config.id,
            new Map([
                ['carm', slotCarm],
                ['toolbar', slotToolbar],
                ['slider', slotSlider],
                ['wl', slotWl],
            ])
        );

        return { wrapper, container };
    }

    private createSlot(name: ViewportSlot, zIndex: number): HTMLElement {
        const el = document.createElement('div');
        el.dataset.slot = name;
        el.style.position = 'absolute';
        el.style.inset = '0';
        el.style.pointerEvents = 'none';
        el.style.zIndex = String(zIndex);
        return el;
    }

    /**
     * Add a placeholder for unsupported viewport types.
     */
    private addPlaceholder(container: HTMLElement, config: ViewportSlotConfig): void {
        const placeholder = document.createElement('div');
        placeholder.style.display = 'flex';
        placeholder.style.alignItems = 'center';
        placeholder.style.justifyContent = 'center';
        placeholder.style.width = '100%';
        placeholder.style.height = '100%';
        placeholder.style.color = '#666';
        placeholder.style.fontSize = '14px';
        placeholder.textContent = `${config.label} (${config.type})`;
        container.appendChild(placeholder);
    }
}
